import {useDebouncedValue, useToggle, useViewportSize} from '@mantine/hooks';
import {useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import {AvatarGroup, Button, Chip, Input, Popover, PopoverContent, PopoverTrigger, Tooltip} from "@heroui/react";
import {useVirtualizer} from '@tanstack/react-virtual';
import {useRecoilState} from 'recoil';
import {userState} from '../../store.jsx';
import {twMerge} from 'tailwind-merge';

const inputWrapper = 'min-h-12s !bg-white border-1 data-[focus=true]:!border-default data-[hover=true]:border-default shadow-none';

export default function Models({models = [], form, valueKey = 'language', ...props}) {
	const [search, setSearch] = useState('')
	const [deboundedSearch] = useDebouncedValue(search, 300)
	const [open, setOpen] = useToggle()
	const [authMe] = useRecoilState(userState)
	const {width, height} = useViewportSize()
	
	const selectedModels = useMemo(() => {
		return _.find(models, (item) => item?.model_id == form.values?.model)
	}, [form.values?.model])
	
	const historyContainerRef = useRef(null);
	const virtualSpeakers = useVirtualizer({
		count: _.size(models || []),
		getScrollElement: () => historyContainerRef.current,
		estimateSize: () => 90,
		gap: 8,
		overscan: 5,
	})
	
	const enabledOrDisabled = (item) => {
		const isPremium = _.lowerCase(item?.speaker_type) === 'premium';
		const isPlanEligible = (authMe?.current_plan || 1) >= 2 || authMe?.trial_status === 'active';
		return isPremium && !isPlanEligible;
	};
	
	return (
		<>
			<Popover
				isOpen={open}
				onOpenChange={setOpen}
				classNames={{
					base: 'w-full max-w-96 w-screen',
				}}
				placement="bottom">
				<PopoverTrigger>
					<Button
						fullWidth variant="bordered"
						endContent={<i className="hgi-stroke hgi-arrow-right-01"></i>}
						className="border border-slate-800/10 min-h-14 !scale-100 justify-between bg-white">
						{selectedModels ?
							<div className="text-left">
								<h5>{selectedModels?.name}</h5>
								<p className="text-tiny truncate">
									{selectedModels?.description || ''}
								</p>
							</div>
							:
							<>
								Select model...
							</>
						}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="p-0">
					<div className="w-full relative px-2 py-3">
						<div className="mb-2 border-b border-slate-200/30 pb-2 hidden">
							<Input
								label=""
								placeholder="Search here..."
								labelPlacement="outside"
								type="text"
								{...form.getInputProps('search_in_history')}
								classNames={{
									inputWrapper,
									label: 'text-slate-500 mb-10 top-5',
								}}
								endContent={form.values?.search_in_history && <Button
									isIconOnly size="sm" radius="full"
									variant="light" color="danger"
									onPress={() => form.setFieldValue('search_in_history', '')}
									children={<i className="hgi-stroke hgi-cancel-01"></i>}
								/>}
							/>
						</div>
						<div
							ref={historyContainerRef}
							style={{
								maxHeight: `${height - 400}px`,
							}}
							className="relative min-h-72 overflow-y-auto">
							{_.size(virtualSpeakers.getVirtualItems()) > 0 ? virtualSpeakers.getVirtualItems()?.map((item) => {
									const itemData = models[ item.index ] || {};
									const isSelected = selectedModels?.model_id == itemData?.model_id;
									return (
										<Tooltip
											color="foreground" showArrow
											isDisabled={!enabledOrDisabled(itemData)}
											content="Upgrade your plan to access this feature.">
											<Button
												key={item.index}
												style={{
													transform: `translateY(${item.start}px)`,
													minHeight: `${item.size}px`,
												}}
												onPress={() => {
													if (!enabledOrDisabled(itemData)) {
														if (!isSelected) {
															form.setFieldValue('model', itemData?.model_id)
															form.setFieldValue('voice_model', itemData?.provider)
															form.setFieldValue('voice_id', '')
														}
														setOpen(false)
													}
												}}
												endContent={isSelected &&
													<i className="hgi-stroke hgi-checkmark-circle-01 text-success-600 text-xl mt-3"></i>}
												className={twMerge([
													"w-full absolute top-0 left-0 bg-default-50/5 border border-default-200/50",
													enabledOrDisabled(itemData) && "!opacity-50"
												])}>
												<div className="text-left w-full transition-all truncate">
													<h3 className="text-slate-700 font-medium truncate mb-2">
														{itemData?.name}
													</h3>
													<div
														className="absolute top-2 right-2">
														<Chip
															size="sm"
															color={itemData?.speaker_type == 'Premium' ? 'warning' : 'default'}
															children={itemData?.speaker_type}
														/>
													</div>
													<div className="flex gap-2 items-center truncate">
														{_.size(itemData?.description) > 0 &&
															<p className="text-slate-700 font-medium text-tiny truncate">
																{itemData?.description}
															</p>}
														<div className="flex gap-1 mt-1 mb-1 items-center">
															<AvatarGroup
																max={2}
																classNames={{
																	base: 'gap-3',
																	count: 'h-6 px-6 !translate-x-0'
																}}
																renderCount={(count) => {
																	const updatedValue = _.drop(itemData?.languages, 3);
																	return (
																		<Tooltip
																			color="foreground"
																			showArrow delay={300}
																			content={<div
																				className="max-h-[30dvh] overflow-y-auto scrollbar-hide">
																				{updatedValue?.map((language, index) => (
																					<span key={index}
																					      className="block w-full text-left mb-1">{language}</span>
																				))}
																			</div>}
																		>
																			<Chip
																				size="sm"
																				children={`+${count}`}
																			/>
																		</Tooltip>
																	)
																}}
															>
																{itemData?.languages?.map((language, index) => (
																	<Chip
																		size="sm"
																		key={index}
																		classNames={{
																			base: 'bg-slate-200',
																			content: '!max-w-18 truncate bg-slate-200',
																		}}
																		children={language}
																	/>
																))
																}
															</AvatarGroup>
														</div>
													</div>
												</div>
											</Button>
										</Tooltip>
									)
								}) :
								<div className="w-full h-full flex justify-center items-center">
									No Content Available
								</div>
							}
						
						</div>
					</div>
				</PopoverContent>
			</Popover>
		</>
	);
}
